import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Menu,
  Moon,
  Sun,
  Monitor,
  LogOut,
  User,
  Shield,
  ChevronRight,
  Building2,
  ChevronsUpDown,
  Check,
  Search,
} from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Avatar, AvatarFallback } from '@shared/components/ui/avatar';
import { Separator } from '@shared/components/ui/separator';
import { Input } from '@shared/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@shared/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shared/components/ui/popover';
import { useUiStore } from '@app/store/ui.store';
import { useAuthStore } from '@app/store/auth.store';
import { useContextStore } from '@app/store/context.store';
import { useTenants } from '@features/tenants/hooks/useTenants';
import { authService } from '@/services';
import {
  canActAsTenant,
  sessionRoleLabel,
  shouldShowTenantSwitcher,
} from '@features/auth/roles';
import { isPlatformRoute } from '../lib/route-scope';
import { cn } from '@shared/lib/utils';
import type { Tenant } from '@/services/tenant.service';

// ── Breadcrumb ────────────────────────────────────────────────────────────────

const BREADCRUMB_MAP: Record<string, string> = {
  '/admin':           'Dashboard',
  '/admin/analytics': 'Analytics',
  '/admin/billing':   'Billing',
  '/admin/platform-billing': 'Platform Billing',
  '/admin/knowledge-gaps': 'Knowledge Gaps',
  '/admin/knowledge-answers': 'KB Answers',
  '/admin/handoffs':  'Handoffs',
  '/admin/tickets':   'Tickets',
  '/admin/meetings':  'Meetings',
  '/admin/call-logs': 'Call Logs',
  '/admin/phone-numbers': 'Phone Numbers',
  '/admin/routing-policies': 'Routing Policies',
  '/admin/live-calls': 'Live Calls',
  '/admin/voice-engines': 'Voice Engines',
  '/admin/telephony-countries': 'Telephony Countries',
  '/admin/porting-requests': 'Porting Requests',
  '/admin/tenants':   'Tenants',
  '/admin/platform-users': 'Platform Users',
  '/admin/platform-roles': 'Platform Roles',
  '/admin/platform-audit': 'Platform Audit',
  '/admin/users':     'Users',
  '/admin/roles':     'Roles',
  '/admin/audit':     'Audit Log',
  '/admin/agents':    'Agents',
  '/admin/knowledge': 'Knowledge Base',
  '/admin/settings':  'Settings',
  '/admin/profile':   'Profile',
  '/admin/security':  'Security',
};

function useBreadcrumb() {
  const { pathname } = useLocation();
  const segments = pathname.split('/').filter(Boolean);
  const crumbs: { label: string; path: string }[] = [];
  let built = '';
  for (const seg of segments) {
    built += `/${seg}`;
    const label = BREADCRUMB_MAP[built];
    if (label) crumbs.push({ label, path: built });
  }
  return crumbs;
}

function getInitials(name?: string | null, email?: string | null): string {
  if (name) return name.split(' ').slice(0, 2).map((s) => s[0]).join('').toUpperCase();
  if (email) return email[0].toUpperCase();
  return 'U';
}

// ── TenantSwitcher ────────────────────────────────────────────────────────────

/**
 * Displays the active tenant and lets the user switch context.
 * Shown for platform act-as users and multi-membership users.
 * Persists via POST /auth/active-tenant and clears agent context.
 */
function TenantSwitcher() {
  const user = useAuthStore((s) => s.user);
  if (canActAsTenant(user)) return <ActAsTenantSwitcher />;
  return <MembershipTenantSwitcher />;
}

function MembershipTenantSwitcher() {
  const user = useAuthStore((s) => s.user);
  const switchActiveTenant = useAuthStore((s) => s.switchActiveTenant);
  const { activeTenant, activeTenantId, restoreActiveTenant, clearActiveAgent } =
    useContextStore();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [switching, setSwitching] = useState(false);

  const options: Tenant[] = (user?.memberships ?? []).map((m) => ({
    id: m.tenantId,
    name: m.tenantName,
    slug: '',
    status: 'active' as const,
  }));

  useEffect(() => {
    if (activeTenant || options.length === 0) return;
    const preferred =
      options.find((t) => t.id === (activeTenantId ?? user?.tenantId)) ??
      options[0];
    if (preferred) restoreActiveTenant(preferred);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- options derived from memberships
  }, [activeTenant, activeTenantId, user?.memberships, user?.tenantId, restoreActiveTenant]);

  return (
    <TenantSwitcherPopover
      options={options}
      activeTenant={activeTenant}
      open={open}
      setOpen={setOpen}
      query={query}
      setQuery={setQuery}
      switching={switching}
      onSelect={async (tenant) => {
        setSwitching(true);
        try {
          await switchActiveTenant(tenant.id);
          clearActiveAgent();
          setOpen(false);
          setQuery('');
        } finally {
          setSwitching(false);
        }
      }}
    />
  );
}

function ActAsTenantSwitcher() {
  const applyActAsTenant = useAuthStore((s) => s.applyActAsTenant);
  const { activeTenant, activeTenantId, restoreActiveTenant, clearActiveAgent } =
    useContextStore();
  const { tenants, loading: tenantsLoading, refetch } = useTenants();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (open) void refetch();
  }, [open, refetch]);

  useEffect(() => {
    if (activeTenant || tenantsLoading || tenants.length === 0) return;
    const activeOptions = tenants.filter((t) => t.status === 'active');
    if (activeTenantId) {
      const match = activeOptions.find((t) => t.id === activeTenantId);
      if (match) {
        restoreActiveTenant(match);
        return;
      }
    }
    if (activeOptions.length === 1) void applyActAsTenant(activeOptions[0]);
  }, [
    activeTenant,
    activeTenantId,
    tenants,
    tenantsLoading,
    restoreActiveTenant,
    applyActAsTenant,
  ]);

  return (
    <TenantSwitcherPopover
      options={tenants.filter((t) => t.status === 'active')}
      activeTenant={activeTenant}
      open={open}
      setOpen={setOpen}
      query={query}
      setQuery={setQuery}
      switching={switching}
      onSelect={async (tenant) => {
        setSwitching(true);
        try {
          await applyActAsTenant(tenant);
          clearActiveAgent();
          setOpen(false);
          setQuery('');
        } finally {
          setSwitching(false);
        }
      }}
    />
  );
}

function TenantSwitcherPopover({
  options,
  activeTenant,
  open,
  setOpen,
  query,
  setQuery,
  switching,
  onSelect,
}: {
  options: Tenant[];
  activeTenant: Tenant | null;
  open: boolean;
  setOpen: (v: boolean) => void;
  query: string;
  setQuery: (v: string) => void;
  switching: boolean;
  onSelect: (tenant: Tenant) => Promise<void>;
}) {
  const filtered = options.filter(
    (t) =>
      t.name.toLowerCase().includes(query.toLowerCase()) ||
      t.slug.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 max-w-[220px] gap-1.5 px-2.5 text-xs font-medium"
          aria-label="Switch tenant"
          disabled={switching}
        >
          <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate">
            {activeTenant?.name ?? 'Select Tenant'}
          </span>
          <ChevronsUpDown className="ml-auto h-3 w-3 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-72 p-0" align="start">
        <div className="flex items-center border-b px-3 py-2 gap-2">
          <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <Input
            placeholder="Search tenants…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-7 border-0 p-0 text-xs shadow-none focus-visible:ring-0"
          />
        </div>

        <div className="max-h-64 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <p className="px-3 py-4 text-center text-xs text-muted-foreground">
              No tenants found
            </p>
          ) : (
            filtered.map((tenant) => {
              const isActive = tenant.id === activeTenant?.id;
              return (
                <button
                  key={tenant.id}
                  type="button"
                  onClick={() => void onSelect(tenant)}
                  disabled={switching}
                  className={cn(
                    'flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs transition-colors',
                    'hover:bg-accent hover:text-accent-foreground',
                    isActive && 'bg-primary/8 font-medium text-primary',
                  )}
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-primary/10 text-[10px] font-bold text-primary">
                    {tenant.name[0].toUpperCase()}
                  </span>
                  <span className="flex-1 truncate">{tenant.name}</span>
                  <span
                    className={cn(
                      'ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold',
                      tenant.status === 'active'
                        ? 'bg-emerald-100 text-emerald-700'
                        : tenant.status === 'suspended'
                          ? 'bg-red-100 text-red-600'
                          : 'bg-slate-100 text-slate-500',
                    )}
                  >
                    {tenant.status}
                  </span>
                  {isActive && <Check className="h-3 w-3 shrink-0 text-primary" />}
                </button>
              );
            })
          )}
        </div>

        <div className="border-t px-3 py-2 text-[10px] text-muted-foreground">
          Switching tenant clears the active agent context.
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── AdminTopNav ───────────────────────────────────────────────────────────────

export function AdminTopNav() {
  const { setMobileNavOpen, theme, setTheme } = useUiStore();
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const crumbs = useBreadcrumb();
  const platformView = isPlatformRoute(pathname);
  const showSwitcher = !platformView && shouldShowTenantSwitcher(user);

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch {
      // Ignore — clear the local session regardless.
    } finally {
      logout();
      navigate('/login', { replace: true });
    }
  };

  return (
    <header className="flex h-16 shrink-0 items-center gap-3 border-b bg-background px-4 md:px-6">
      {/* Mobile: hamburger opens drawer (separate from desktop expand/collapse) */}
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden"
        onClick={() => setMobileNavOpen(true)}
        aria-label="Open navigation"
      >
        <Menu className="h-5 w-5" />
      </Button>

      {/* ── Breadcrumb ──────────────────────────────────────────────────── */}
      <nav
        className="hidden items-center gap-1.5 text-sm md:flex"
        aria-label="Breadcrumb"
      >
        {crumbs.map((crumb, i) => (
          <span key={crumb.path} className="flex items-center gap-1.5">
            {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
            <span
              className={cn(
                i === crumbs.length - 1
                  ? 'font-semibold text-foreground'
                  : 'text-muted-foreground',
              )}
            >
              {crumb.label}
            </span>
          </span>
        ))}
      </nav>

      {/* ── Right side ──────────────────────────────────────────────────── */}
      <div className="ml-auto flex items-center gap-2">

        {platformView ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-md border border-primary/20 bg-primary/5 px-2.5 py-1 text-xs font-medium text-primary"
            title="This view shows data across all tenants"
          >
            <Building2 className="h-3.5 w-3.5" />
            Platform · all tenants
          </span>
        ) : (
          showSwitcher && <TenantSwitcher />
        )}

        <Separator orientation="vertical" className="h-6" />

        {/* User dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex h-9 items-center gap-2 rounded-lg px-2">
              <Avatar className="h-7 w-7">
                <AvatarFallback className="bg-primary text-[11px] font-bold text-primary-foreground">
                  {getInitials(user?.fullName, user?.email)}
                </AvatarFallback>
              </Avatar>
              <div className="hidden flex-col items-start md:flex">
                <span className="text-xs font-semibold leading-none">
                  {user?.fullName ?? 'Admin User'}
                </span>
                <span className="text-[10px] leading-none text-muted-foreground">
                  {user?.email ?? 'admin@example.com'}
                </span>
              </div>
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold">{user?.fullName ?? 'Admin User'}</span>
              <span className="text-xs font-normal text-muted-foreground">
                {user?.email ?? 'admin@example.com'}
              </span>
              {user && (
                <span className="mt-1 w-fit rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                  {sessionRoleLabel(user)}
                </span>
              )}
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            <DropdownMenuItem onClick={() => navigate('/admin/profile')}>
              <User className="mr-2 h-4 w-4" />Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/admin/security')}>
              <Shield className="mr-2 h-4 w-4" />Security
            </DropdownMenuItem>

            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                {theme === 'dark' ? <Moon className="mr-2 h-4 w-4" />
                  : theme === 'light' ? <Sun className="mr-2 h-4 w-4" />
                  : <Monitor className="mr-2 h-4 w-4" />}
                Theme
                <span className="ml-auto pl-2 text-[10px] font-normal capitalize text-muted-foreground">
                  {theme}
                </span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem onClick={() => setTheme('light')}>
                  <Sun className="mr-2 h-4 w-4" />
                  Light
                  {theme === 'light' && <Check className="ml-auto h-4 w-4" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('dark')}>
                  <Moon className="mr-2 h-4 w-4" />
                  Dark
                  {theme === 'dark' && <Check className="ml-auto h-4 w-4" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('system')}>
                  <Monitor className="mr-2 h-4 w-4" />
                  System
                  {theme === 'system' && <Check className="ml-auto h-4 w-4" />}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={handleLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
